import { useCallback, useEffect, useRef, useState } from "react";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  LinkSimple,
  DownloadSimple,
  CircleNotch,
} from "@phosphor-icons/react";
import { RSpinBlock } from "@/components/RSpin";

import { PAGE_SIZE } from "./pub_brands/constants";
import { CountryChips, NetworkChips, SearchBox } from "./pub_brands/BrandsFilters";
import BrandRow from "./pub_brands/BrandRow";
import TryTrackerDialog from "./pub_brands/TryTrackerDialog";
import useBrandsCsvExport from "./pub_brands/useBrandsCsvExport";

/**
 * Publisher's "My Brands" — the campaigns tab where pubs grab their
 * tracking URLs and inspect program terms.
 *
 * Server-paginated: first paint loads `PAGE_SIZE` rows; "Show more"
 * fetches the next page and appends. Filters (country / network /
 * search) are sent to the API so big portfolios never download the
 * full hydrated list up front.
 */
export default function PubBrands() {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [activeCount, setActiveCount] = useState(0);
  const [portfolioTotal, setPortfolioTotal] = useState(0);
  const [countryStats, setCountryStats] = useState({ top: [], global: 0, total: 0 });
  const [networkStats, setNetworkStats] = useState([]);
  const [discloseNetworks, setDiscloseNetworks] = useState(false);

  const [country, setCountry] = useState("all");
  const [network, setNetwork] = useState("all");
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [tryItBrand, setTryItBrand] = useState(null);

  const { exportState, downloadCsv, isExporting } = useBrandsCsvExport();
  const fetchGen = useRef(0);

  // Debounce search so we don't hit the API on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query), 300);
    return () => clearTimeout(t);
  }, [query]);

  const fetchPage = useCallback(async (pageNum, { append }) => {
    const gen = ++fetchGen.current;
    if (append) setLoadingMore(true);
    else setLoading(true);
    try {
      const params = {
        page: pageNum,
        page_size: PAGE_SIZE,
      };
      if (debouncedQuery.trim()) params.q = debouncedQuery.trim();
      if (network !== "all") params.network = network;
      if (country !== "all") params.country = country;

      const { data } = await API.get("/publisher/brands", { params });
      if (gen !== fetchGen.current) return; // stale response

      const items = data?.items || [];
      setBrands((prev) => (append ? [...prev, ...items] : items));
      setPage(data?.page || pageNum);
      setHasMore(Boolean(data?.has_more));
      setTotal(data?.total || 0);
      setActiveCount(data?.active_count || 0);
      setDiscloseNetworks(Boolean(data?.disclose_networks));
      if (data?.country_stats) {
        setCountryStats(data.country_stats);
        setPortfolioTotal(data.country_stats.total || 0);
      }
      if (Array.isArray(data?.network_stats)) {
        setNetworkStats(data.network_stats);
      }
    } catch (err) {
      console.error(err);
      if (!append && gen === fetchGen.current) {
        setBrands([]);
        setTotal(0);
        setHasMore(false);
      }
    } finally {
      if (gen === fetchGen.current) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  }, [debouncedQuery, network, country]);

  // Reset + refetch whenever filters change.
  useEffect(() => {
    setExpandedId(null);
    fetchPage(1, { append: false });
  }, [fetchPage]);

  const loadMore = () => {
    if (loadingMore || !hasMore) return;
    fetchPage(page + 1, { append: true });
  };

  const remaining = Math.max(0, total - brands.length);
  const emptyPortfolio = !loading && portfolioTotal === 0 && brands.length === 0;
  const emptyFiltered = !loading && !emptyPortfolio && brands.length === 0;

  return (
    <div className="p-8" data-testid="publisher-brands-page">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <p className="text-[11px] font-bold tracking-[0.3em] uppercase text-slate-400 mb-2">
            Campaigns / Assigned Brands
          </p>
          <h1 className="font-heading text-3xl font-bold text-slate-900 tracking-tight leading-none">My Brands</h1>
          <p className="text-sm text-slate-500 mt-2">
            {activeCount} active
            {total !== portfolioTotal && portfolioTotal > 0 && (
              <span className="text-slate-400"> · {total.toLocaleString()} match filters</span>
            )}
            {country !== "all" && (
              <>
                {" "}in <span className="font-semibold text-slate-700">{country}</span>
              </>
            )}
            {portfolioTotal > 0 && (
              <span className="text-slate-400"> · {portfolioTotal.toLocaleString()} total</span>
            )}
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => downloadCsv({ network, country, q: query })}
          disabled={isExporting || total === 0}
          className="h-9 shrink-0"
          title={
            network !== "all"
              ? `Download CSV for ${network} brands only`
              : country !== "all"
                ? `Download CSV for ${country} brands only`
                : "Download all assigned brands as CSV"
          }
          data-testid="pub-brands-download-csv"
        >
          {isExporting ? (
            <>
              <CircleNotch size={16} className="mr-2 animate-spin" />
              Preparing…
            </>
          ) : (
            <>
              <DownloadSimple size={16} className="mr-2" />
              {network !== "all" ? `Export ${network}` : "Download CSV"}
            </>
          )}
        </Button>
      </div>

      {exportState.phase !== "idle" && (
        <div
          className="mb-5 border border-slate-200 bg-slate-50 rounded-md p-3 flex items-center gap-3"
          data-testid="pub-brands-export-progress"
        >
          {exportState.phase === "done" ? (
            <DownloadSimple size={18} className="text-emerald-600 shrink-0" weight="bold" />
          ) : (
            <CircleNotch size={18} className="text-[#002FA7] animate-spin shrink-0" />
          )}
          <div className="flex-1 min-w-0">
            <div className="text-xs font-semibold text-slate-700">
              {exportState.phase === "preparing" && "Preparing CSV…"}
              {exportState.phase === "streaming" && (
                <>
                  Downloading {exportState.total > 0
                    ? <>~<span className="font-mono">{exportState.total.toLocaleString()}</span> brands</>
                    : "your brands"}
                  …
                </>
              )}
              {exportState.phase === "done" && (
                <>Ready · <span className="font-mono">{exportState.total.toLocaleString()}</span> brands exported</>
              )}
            </div>
            <div className="text-[10px] text-slate-500 font-mono mt-0.5 flex gap-3">
              <span>{(exportState.received / 1024).toFixed(1)} KB received</span>
              <span>{(exportState.elapsedMs / 1000).toFixed(1)}s elapsed</span>
            </div>
          </div>
        </div>
      )}

      <CountryChips
        country={country}
        onChange={setCountry}
        portfolioTotal={portfolioTotal}
        countryStats={countryStats}
      />
      <NetworkChips
        network={network}
        onChange={setNetwork}
        portfolioTotal={portfolioTotal}
        networkStats={networkStats}
        discloseNetworks={discloseNetworks}
      />
      <SearchBox query={query} onChange={setQuery} />

      {loading ? (
        <RSpinBlock label="Loading your brands" height={260} data-testid="pub-brands-spinner" />
      ) : emptyPortfolio ? (
        <div className="border border-slate-200 rounded-md p-12 text-center">
          <LinkSimple size={32} className="mx-auto text-slate-300 mb-3" />
          <p className="text-sm text-slate-500">No brands assigned yet. Contact admin to get started.</p>
        </div>
      ) : emptyFiltered ? (
        <div className="border border-slate-200 rounded-md p-12 text-center">
          <LinkSimple size={32} className="mx-auto text-slate-300 mb-3" />
          <p className="text-sm text-slate-500">
            {query
              ? <>No brands match <span className="font-semibold">"{query}"</span>{country !== "all" && <> in <span className="font-semibold">{country}</span></>}.</>
              : <>No brands available in <span className="font-semibold">{country !== "all" ? country : "this filter"}</span>.</>
            }{" "}
            <button onClick={() => { setQuery(""); setCountry("all"); setNetwork("all"); }} className="text-[#002FA7] underline hover:no-underline">
              Show all
            </button>
          </p>
        </div>
      ) : (
        <>
          <div className="border border-slate-200 rounded-md divide-y divide-slate-100 overflow-hidden bg-white">
            {brands.map((b) => (
              <BrandRow
                key={b.brand_id}
                brand={b}
                isOpen={expandedId === b.brand_id}
                onToggleOpen={() => setExpandedId(expandedId === b.brand_id ? null : b.brand_id)}
                onTryTracker={setTryItBrand}
              />
            ))}
          </div>
          {hasMore && (
            <div className="mt-4 flex items-center justify-center" data-testid="pub-brands-show-more-wrap">
              <Button
                variant="outline"
                onClick={loadMore}
                disabled={loadingMore}
                data-testid="pub-brands-show-more"
              >
                {loadingMore ? (
                  <>
                    <CircleNotch size={16} className="mr-2 animate-spin" />
                    Loading…
                  </>
                ) : (
                  <>Show more ({remaining.toLocaleString()} remaining)</>
                )}
              </Button>
            </div>
          )}
        </>
      )}

      {tryItBrand && (
        <TryTrackerDialog brand={tryItBrand} onClose={() => setTryItBrand(null)} />
      )}
    </div>
  );
}
